import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Empty,
  Progress,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  DislikeOutlined,
  ExclamationCircleOutlined,
  LikeOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  aiApprovalBriefService,
  type ApprovalBriefClaim,
  type ApprovalBriefMetric,
  type ApprovalBriefPolicyCheck,
  type ApprovalBriefProjectHealth,
  type ApprovalBriefRisk,
  type ApprovalBriefRiskLevel,
  type ProjectHealthLevel,
} from '@/services/aiApprovalBrief.service';
import { aiGovernanceService } from '@/services/aiGovernance.service';

const { Text, Paragraph, Title } = Typography;

const COMMERCIAL_SUMMARY_LABELS = ['Sell', 'Cost', 'Margin', 'Margin %', 'Discount', 'Validity'] as const;

function MetricsTable({
  metrics,
  title,
  compact,
}: {
  metrics: ApprovalBriefMetric[];
  title?: string;
  compact?: boolean;
}) {
  if (!metrics.length) return null;
  return (
    <div>
      {title ? <SectionTitle>{title}</SectionTitle> : null}
      <Table
        size="small"
        pagination={false}
        rowKey={(r) => r.label}
        columns={[
          {
            title: 'Metric',
            dataIndex: 'label',
            key: 'label',
            width: compact ? '42%' : '40%',
            render: (v: string) => <Text type="secondary">{v}</Text>,
          },
          {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
            render: (v: string) => <Text strong>{v}</Text>,
          },
        ]}
        dataSource={metrics}
        style={{ marginBottom: title ? 0 : 8 }}
      />
    </div>
  );
}

function splitCommercialSummaryMetrics(metrics: ApprovalBriefMetric[]): {
  commercial: ApprovalBriefMetric[];
  other: ApprovalBriefMetric[];
} {
  const commercial: ApprovalBriefMetric[] = [];
  const other: ApprovalBriefMetric[] = [];
  const wanted = new Set<string>(COMMERCIAL_SUMMARY_LABELS);
  for (const m of metrics) {
    if (wanted.has(m.label)) commercial.push(m);
    else other.push(m);
  }
  // Keep Commercial Summary row order stable (Sell → Validity)
  commercial.sort(
    (a, b) =>
      COMMERCIAL_SUMMARY_LABELS.indexOf(a.label as (typeof COMMERCIAL_SUMMARY_LABELS)[number]) -
      COMMERCIAL_SUMMARY_LABELS.indexOf(b.label as (typeof COMMERCIAL_SUMMARY_LABELS)[number])
  );
  return { commercial, other };
}

function riskColor(level: ApprovalBriefRiskLevel): string {
  if (level === 'CRITICAL' || level === 'HIGH') return 'red';
  if (level === 'MEDIUM') return 'orange';
  if (level === 'LOW') return 'green';
  return 'default';
}

function projectHealthColor(health: ProjectHealthLevel): string {
  if (health === 'Financial risk' || health === 'Cancelled') return 'red';
  if (health === 'Attention needed') return 'orange';
  return 'green';
}

function policyColor(status: ApprovalBriefPolicyCheck['status']): string {
  if (status === 'pass') return 'success';
  if (status === 'warn') return 'warning';
  if (status === 'fail') return 'error';
  return 'default';
}

function ClaimBlock({ claim, defaultOpen = false }: { claim: ApprovalBriefClaim; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 8 }}>
      <Paragraph style={{ marginBottom: 4 }}>{claim.text}</Paragraph>
      {claim.evidence?.length > 0 && (
        <>
          <Button type="link" size="small" icon={<QuestionCircleOutlined />} onClick={() => setOpen((v) => !v)} style={{ padding: 0 }}>
            {open ? 'Hide evidence' : 'Why?'}
          </Button>
          {open && (
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {claim.evidence.map((e, i) => (
                <li key={`${e.source}-${i}`}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {e.source}
                    {e.ref ? ` · ${e.ref}` : ''}: {e.detail}
                  </Text>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Text strong style={{ display: 'block', marginBottom: 6 }}>
      {children}
    </Text>
  );
}

const PROJECT_HEALTH_COMPACT_LABELS = [
  'Health',
  'PO ordered',
  'GRN received',
  'Supplier invoiced',
  'Supplier paid',
  'Committed cost',
  'Forecast margin',
  'Past-due AR',
] as const;

function ProjectHealthSection({
  projectHealth,
  compact = false,
}: {
  projectHealth: ApprovalBriefProjectHealth;
  compact?: boolean;
}) {
  if (!projectHealth.projects.length && !projectHealth.gaps.length) return null;

  const compactWanted = new Set<string>(PROJECT_HEALTH_COMPACT_LABELS);

  return (
    <div>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {projectHealth.projects.map((p, idx) => {
          const label = [p.projectNumber, p.projectName].filter(Boolean).join(' — ') || p.projectId;
          const blockers = compact ? p.blockers.slice(0, 2) : p.blockers;
          const metrics = (compact ? p.metrics.filter((m) => compactWanted.has(m.label)) : p.metrics).map(
            (m) =>
              m.label === 'Health'
                ? { ...m, value: p.health }
                : m
          );
          const tableTitle =
            projectHealth.projects.length === 1 && idx === 0
              ? 'Project Health'
              : `Project Health · ${p.projectNumber || label}`;
          return (
            <div key={p.projectId}>
              <Space wrap style={{ marginBottom: 6 }}>
                <Link to={`/prj/projects/${p.projectId}/status-check`}>{label}</Link>
                <Tag color={projectHealthColor(p.health)}>{p.health}</Tag>
                {projectHealth.multiProject && idx === 0 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    multiple projects
                  </Text>
                ) : null}
              </Space>
              <MetricsTable title={tableTitle} metrics={metrics} compact={compact} />
              {blockers.map((c, i) => (
                <ClaimBlock key={`${p.projectId}-b-${i}`} claim={c} />
              ))}
              {compact && p.blockers.length > blockers.length ? (
                <Text type="secondary">+{p.blockers.length - blockers.length} more blockers in Details</Text>
              ) : null}
            </div>
          );
        })}
        {projectHealth.gaps.map((c, i) => (
          <ClaimBlock key={`ph-gap-${i}`} claim={c} />
        ))}
      </Space>
    </div>
  );
}

function ExecutiveContextCard({ entityType, entityId }: { entityType: string; entityId: string }) {
  const [enabled, setEnabled] = useState(false);
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['ai-approval-brief-executive', entityType, entityId],
    queryFn: () => aiApprovalBriefService.getExecutiveContext(entityType, entityId),
    enabled: enabled && !!entityType && !!entityId,
    staleTime: 60_000,
    retry: 1,
  });

  if (!enabled) {
    return (
      <Button size="small" type="primary" ghost onClick={() => setEnabled(true)}>
        Load executive context
      </Button>
    );
  }
  if (isLoading) return <Spin size="small" />;
  if (isError) {
    const ax = error as { message?: string } | null;
    return (
      <Space>
        <Alert type="info" showIcon message={ax?.message || 'Executive context unavailable'} />
        <Button size="small" onClick={() => void refetch()}>
          Retry
        </Button>
      </Space>
    );
  }
  if (!data) return <Empty description="No executive context" />;
  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Tag color="purple">Lens: {data.lens}</Tag>
      <ClaimBlock claim={data.headline} />
      {data.highlightedMetrics.length > 0 && (
        <MetricsTable
          metrics={(() => {
            const { commercial } = splitCommercialSummaryMetrics(data.highlightedMetrics);
            return commercial.length > 0 ? commercial : data.highlightedMetrics;
          })()}
          compact
        />
      )}
      {data.focusAreas.map((c, i) => (
        <ClaimBlock key={`exec-f-${i}`} claim={c} />
      ))}
    </Space>
  );
}

function RiskRow({ risk }: { risk: ApprovalBriefRisk }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <Space>
        <Tag color={riskColor(risk.level)}>{risk.level}</Tag>
        <Text strong>{risk.category}</Text>
      </Space>
      <ClaimBlock claim={{ text: risk.summary, evidence: risk.evidence }} />
    </div>
  );
}

export interface AiApprovalBriefPanelProps {
  entityType: string;
  entityId: string;
  /** Expand Decision intelligence section by default (app-wide default: collapsed) */
  defaultExpanded?: boolean;
}

export const AiApprovalBriefPanel: React.FC<AiApprovalBriefPanelProps> = ({
  entityType,
  entityId,
  defaultExpanded = false,
}) => {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['ai-approval-brief', entityType, entityId],
    queryFn: () => aiApprovalBriefService.getBrief(entityType, entityId),
    enabled: !!entityType && !!entityId,
    staleTime: 60_000,
    retry: 1,
  });

  const errorMessage = (() => {
    const ax = error as { response?: { data?: { message?: string } }; message?: string } | null;
    return ax?.response?.data?.message || ax?.message || 'Could not load brief';
  })();

  const refresh = async () => {
    await aiApprovalBriefService.getBrief(entityType, entityId, true);
    await queryClient.invalidateQueries({ queryKey: ['ai-approval-brief', entityType, entityId] });
  };

  const feedbackMutation = useMutation({
    mutationFn: (rating: 'helpful' | 'not_helpful') =>
      aiGovernanceService.submitFeedback({
        entityType,
        entityId,
        contentHash: data?.contentHash,
        rating,
      }),
    onSuccess: () => message.success('Thanks — feedback recorded for governance (not used for auto-learning)'),
    onError: (e: Error) => message.error(e.message || 'Feedback failed'),
  });

  const analysisStatus =
    data?.analysisStatus ||
    (data?.coverage === 'stub' ? 'STUB' : data?.coverage === 'full' ? 'FULL' : undefined);

  const header = (
    <Space wrap>
      <span>AI Approval Brief</span>
      {analysisStatus && (
        <Tag color={analysisStatus === 'FULL' ? 'green' : analysisStatus === 'PARTIAL' ? 'orange' : 'default'}>
          {analysisStatus}
        </Tag>
      )}
      {data?.coverage === 'stub' && <Tag>Limited coverage</Tag>}
      {data?.stale && <Tag color="orange">Possibly stale</Tag>}
      {data?.narrativeGenerated && <Tag color="blue">Narrative</Tag>}
      {typeof data?.confidenceScore?.percent === 'number' && (
        <Tag color={data.confidenceScore.percent >= 70 ? 'green' : data.confidenceScore.percent >= 45 ? 'orange' : 'red'}>
          Confidence {data.confidenceScore.percent}%
        </Tag>
      )}
      {data?.governance?.trustBand && (
        <Tag color="geekblue">
          Trust {data.governance.trustScore}% · {data.governance.trustBand}
        </Tag>
      )}
    </Space>
  );

  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={header}
      extra={
        <Button
          size="small"
          icon={<ReloadOutlined spin={isFetching} />}
          onClick={() => void refresh()}
          disabled={!entityId || isFetching}
        >
          Refresh
        </Button>
      }
    >
      {isLoading && <Spin />}
      {isError && (
        <Alert
          type="warning"
          showIcon
          message="AI Approval Brief unavailable"
          description={errorMessage}
        />
      )}
      {!isLoading && !isError && !data && <Empty description="No brief" />}
      {data && (
        <Collapse
          defaultActiveKey={defaultExpanded ? ['brief'] : []}
          items={[
            {
              key: 'brief',
              label: data.documentNumber
                ? `${data.documentNumber}${data.documentStatus ? ` · ${data.documentStatus}` : ''}`
                : 'Decision intelligence',
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {data.stale && (
                    <Alert
                      type="warning"
                      showIcon
                      message="Source data may have changed since this brief was generated. Refresh before relying on it."
                    />
                  )}
                  {data.coverage === 'stub' && (
                    <Alert
                      type="info"
                      showIcon
                      message="Full AI intelligence is enabled for Purchase Orders, Order Review Checklist, Contract Review, Customer Orders, and Payment Vouchers. Other document types show a grounded stub only."
                    />
                  )}
                  {analysisStatus === 'PARTIAL' && data.coverage === 'full' && (
                    <Alert
                      type="info"
                      showIcon
                      message="Analysis is partial — some evidence sources are unavailable. Recommendation language is softened."
                    />
                  )}

                  {data.executiveSummary && (
                    <div>
                      <Title level={5} style={{ marginTop: 0 }}>
                        Executive Summary
                      </Title>
                      <ClaimBlock claim={data.executiveSummary} />
                    </div>
                  )}

                  {(() => {
                    const { commercial } = splitCommercialSummaryMetrics(
                      data.financialImpact?.metrics || []
                    );
                    if (commercial.length === 0) return null;
                    // Any project-linked brief (PO, SO, PR, …) or quotation emits Sell/Cost/…
                    return <MetricsTable title="Commercial Summary" metrics={commercial} compact />;
                  })()}

                  {data.projectHealth &&
                    (data.projectHealth.projects.length > 0 || data.projectHealth.gaps.length > 0) && (
                      <ProjectHealthSection projectHealth={data.projectHealth} compact />
                    )}

                  {data.recommendation && (
                    <Alert
                      type="success"
                      showIcon
                      icon={<CheckCircleOutlined />}
                      message="AI Recommendation (advisory only)"
                      description={<ClaimBlock claim={data.recommendation} />}
                    />
                  )}

                  <div>
                    <SectionTitle>Was this brief helpful?</SectionTitle>
                    <Space>
                      <Button
                        size="small"
                        icon={<LikeOutlined />}
                        loading={feedbackMutation.isPending}
                        onClick={() => feedbackMutation.mutate('helpful')}
                      >
                        Helpful
                      </Button>
                      <Button
                        size="small"
                        icon={<DislikeOutlined />}
                        loading={feedbackMutation.isPending}
                        onClick={() => feedbackMutation.mutate('not_helpful')}
                      >
                        Not helpful
                      </Button>
                    </Space>
                    {data.governance?.predictionIds?.length ? (
                      <div style={{ marginTop: 6 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {data.governance.predictionIds.length} prediction(s) registered · schema{' '}
                          {data.governance.versions.schemaVersion}
                        </Text>
                      </div>
                    ) : null}
                  </div>

                  {data.itemsRequiringAttention.length > 0 && (
                    <div>
                      <SectionTitle>
                        <WarningOutlined /> Items Requiring Attention
                      </SectionTitle>
                      {data.itemsRequiringAttention.slice(0, 3).map((c, i) => (
                        <ClaimBlock key={`att-${i}`} claim={c} />
                      ))}
                      {data.itemsRequiringAttention.length > 3 && (
                        <Text type="secondary">+{data.itemsRequiringAttention.length - 3} more in Details</Text>
                      )}
                    </div>
                  )}

                  {data.riskAssessment.length > 0 && (
                    <div>
                      <SectionTitle>Risk Overview</SectionTitle>
                      {data.riskAssessment.map((r) => (
                        <RiskRow key={r.category} risk={r} />
                      ))}
                    </div>
                  )}

                  <div>
                    <SectionTitle>Confidence Score</SectionTitle>
                    <Progress
                      percent={data.confidenceScore.percent}
                      status={data.confidenceScore.percent < 45 ? 'exception' : 'active'}
                      size="small"
                    />
                    {data.confidenceScore.factors.map((c, i) => (
                      <ClaimBlock key={`cf-${i}`} claim={c} />
                    ))}
                  </div>

                  <Collapse
                    size="small"
                    items={[
                      ...(data.decisionIntelligence
                        ? [
                            {
                              key: 'decision-intel',
                              label: 'Decision Intelligence (impact, history, alternatives, contradictions)',
                              children: (
                                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                  <div>
                                    <SectionTitle>Impact</SectionTitle>
                                    <Text type="secondary">If approved</Text>
                                    {data.decisionIntelligence.impacts.onApprove.map((c, i) => (
                                      <ClaimBlock key={`di-oa-${i}`} claim={c} />
                                    ))}
                                    <Text type="secondary">If rejected</Text>
                                    {data.decisionIntelligence.impacts.onReject.map((c, i) => (
                                      <ClaimBlock key={`di-or-${i}`} claim={c} />
                                    ))}
                                  </div>
                                  <div>
                                    <SectionTitle>Historical</SectionTitle>
                                    {(data.decisionIntelligence.historical.similarCount != null ||
                                      data.decisionIntelligence.historical.avgDaysToApprove != null) && (
                                      <Paragraph style={{ marginBottom: 8 }}>
                                        Similar: {data.decisionIntelligence.historical.similarCount ?? '—'}
                                        {data.decisionIntelligence.historical.avgValue != null
                                          ? ` · Avg value ${data.decisionIntelligence.historical.avgValue}`
                                          : ''}
                                        {data.decisionIntelligence.historical.avgDaysToApprove != null
                                          ? ` · Avg ${data.decisionIntelligence.historical.avgDaysToApprove} days to approve`
                                          : ''}
                                      </Paragraph>
                                    )}
                                    {data.decisionIntelligence.historical.commentary.map((c, i) => (
                                      <ClaimBlock key={`di-h-${i}`} claim={c} />
                                    ))}
                                    {data.decisionIntelligence.historical.gaps.map((c, i) => (
                                      <ClaimBlock key={`di-hg-${i}`} claim={c} />
                                    ))}
                                  </div>
                                  {(data.decisionIntelligence.patterns.items.length > 0 ||
                                    data.decisionIntelligence.patterns.gaps.length > 0) && (
                                    <div>
                                      <SectionTitle>Patterns</SectionTitle>
                                      {data.decisionIntelligence.patterns.items.map((c, i) => (
                                        <ClaimBlock key={`di-p-${i}`} claim={c} />
                                      ))}
                                      {data.decisionIntelligence.patterns.gaps.map((c, i) => (
                                        <ClaimBlock key={`di-pg-${i}`} claim={c} />
                                      ))}
                                    </div>
                                  )}
                                  {(data.decisionIntelligence.knowledge.items.length > 0 ||
                                    data.decisionIntelligence.knowledge.gaps.length > 0) && (
                                    <div>
                                      <SectionTitle>Enterprise Knowledge (live ERP)</SectionTitle>
                                      {data.decisionIntelligence.knowledge.items.map((c, i) => (
                                        <ClaimBlock key={`di-k-${i}`} claim={c} />
                                      ))}
                                      {data.decisionIntelligence.knowledge.gaps.map((c, i) => (
                                        <ClaimBlock key={`di-kg-${i}`} claim={c} />
                                      ))}
                                    </div>
                                  )}
                                  {data.decisionIntelligence.enterpriseMemory &&
                                    (data.decisionIntelligence.enterpriseMemory.items.length > 0 ||
                                      data.decisionIntelligence.enterpriseMemory.gaps.length > 0 ||
                                      data.decisionIntelligence.enterpriseMemory.contradictions.length >
                                        0) && (
                                      <div>
                                        <SectionTitle>Validated Organizational Memory</SectionTitle>
                                        {data.decisionIntelligence.enterpriseMemory.items.map((c, i) => (
                                          <ClaimBlock key={`di-em-${i}`} claim={c} />
                                        ))}
                                        {data.decisionIntelligence.enterpriseMemory.contradictions.map(
                                          (c, i) => (
                                            <div key={`di-emc-${i}`} style={{ marginBottom: 8 }}>
                                              <Space>
                                                <Tag color={riskColor(c.severity)}>{c.severity}</Tag>
                                                <Text type="secondary">
                                                  Memory conflict · {c.left.slice(0, 8)}… vs {c.right.slice(0, 8)}…
                                                </Text>
                                              </Space>
                                              <ClaimBlock claim={{ text: c.summary, evidence: c.evidence }} />
                                            </div>
                                          )
                                        )}
                                        {data.decisionIntelligence.enterpriseMemory.gaps.map((c, i) => (
                                          <ClaimBlock key={`di-emg-${i}`} claim={c} />
                                        ))}
                                      </div>
                                    )}
                                  {data.decisionIntelligence.alternatives.length > 0 && (
                                    <div>
                                      <SectionTitle>Alternatives</SectionTitle>
                                      {data.decisionIntelligence.alternatives.map((a, i) => (
                                        <ClaimBlock
                                          key={`di-alt-${i}`}
                                          claim={{ text: `[${a.kind}] ${a.summary}`, evidence: a.evidence }}
                                        />
                                      ))}
                                    </div>
                                  )}
                                  {data.decisionIntelligence.contradictions.length > 0 && (
                                    <div>
                                      <SectionTitle>Contradictions</SectionTitle>
                                      {data.decisionIntelligence.contradictions.map((c, i) => (
                                        <div key={`di-ctr-${i}`} style={{ marginBottom: 8 }}>
                                          <Space>
                                            <Tag color={riskColor(c.severity)}>{c.severity}</Tag>
                                            <Text type="secondary">
                                              {c.left} vs {c.right}
                                            </Text>
                                          </Space>
                                          <ClaimBlock claim={{ text: c.summary, evidence: c.evidence }} />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {data.decisionIntelligence.reasoningChains.length > 0 && (
                                    <div>
                                      <SectionTitle>Reasoning Chain</SectionTitle>
                                      <ol style={{ margin: 0, paddingLeft: 18 }}>
                                        {data.decisionIntelligence.reasoningChains.map((n) => (
                                          <li key={n.id} style={{ marginBottom: 6 }}>
                                            <Text strong>{n.category}</Text>
                                            {n.level ? <Tag style={{ marginLeft: 6 }}>{n.level}</Tag> : null}
                                            <div>
                                              <Text type="secondary">{n.evidenceSummary}</Text>
                                            </div>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                              Rule: {n.ruleId}
                                            </Text>
                                          </li>
                                        ))}
                                      </ol>
                                    </div>
                                  )}
                                </Space>
                              ),
                            },
                          ]
                        : []),
                      {
                        key: 'executive-lazy',
                        label: 'Executive Decision Context (lazy)',
                        children: <ExecutiveContextCard entityType={entityType} entityId={entityId} />,
                      },
                      {
                        key: 'details',
                        label: 'Expand details (context, finance, commercial, policy, questions)',
                        children: (
                          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            {(data.financialImpact.metrics.length > 0 || data.financialImpact.commentary) && (
                              <div>
                                {(() => {
                                  const { commercial, other } = splitCommercialSummaryMetrics(
                                    data.financialImpact.metrics
                                  );
                                  return (
                                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                      {commercial.length > 0 ? (
                                        <MetricsTable title="Commercial Summary" metrics={commercial} />
                                      ) : (
                                        <SectionTitle>Financial Impact</SectionTitle>
                                      )}
                                      {other.length > 0 && (
                                        <MetricsTable
                                          title={commercial.length > 0 ? 'Other financial metrics' : undefined}
                                          metrics={other}
                                        />
                                      )}
                                      {data.financialImpact.commentary && (
                                        <ClaimBlock claim={data.financialImpact.commentary} />
                                      )}
                                    </Space>
                                  );
                                })()}
                              </div>
                            )}
                            {data.projectHealth &&
                              (data.projectHealth.projects.length > 0 ||
                                data.projectHealth.gaps.length > 0) && (
                                <ProjectHealthSection projectHealth={data.projectHealth} />
                              )}
                            {data.itemsRequiringAttention.length > 3 && (
                              <div>
                                <SectionTitle>All Attention Items</SectionTitle>
                                {data.itemsRequiringAttention.map((c, i) => (
                                  <ClaimBlock key={`att-all-${i}`} claim={c} />
                                ))}
                              </div>
                            )}
                            {data.businessContext.length > 0 && (
                              <div>
                                <SectionTitle>Business Context</SectionTitle>
                                {data.businessContext.map((c, i) => (
                                  <ClaimBlock key={`bc-${i}`} claim={c} />
                                ))}
                              </div>
                            )}
                            {data.supplierIntelligence && (
                              <div>
                                <SectionTitle>Supplier Intelligence</SectionTitle>
                                {data.supplierIntelligence.metrics.map((m) => (
                                  <div key={m.label} style={{ marginBottom: 4 }}>
                                    <Text type="secondary">{m.label}: </Text>
                                    <Text strong>{m.value}</Text>
                                  </div>
                                ))}
                                {data.supplierIntelligence.commentary && (
                                  <ClaimBlock claim={data.supplierIntelligence.commentary} />
                                )}
                              </div>
                            )}
                            {data.commercialIntelligence.length > 0 && (
                              <div>
                                <SectionTitle>Commercial Intelligence</SectionTitle>
                                {data.commercialIntelligence.map((c, i) => (
                                  <ClaimBlock key={`ci-${i}`} claim={c} />
                                ))}
                              </div>
                            )}
                            {data.historicalComparison.length > 0 && (
                              <div>
                                <SectionTitle>Historical Comparison</SectionTitle>
                                {data.historicalComparison.map((c, i) => (
                                  <ClaimBlock key={`hc-${i}`} claim={c} />
                                ))}
                              </div>
                            )}
                            {data.policyCompliance.length > 0 && (
                              <div>
                                <SectionTitle>Policy Compliance</SectionTitle>
                                {data.policyCompliance.map((p) => (
                                  <div key={p.label} style={{ marginBottom: 6 }}>
                                    <Space>
                                      <Tag
                                        color={policyColor(p.status)}
                                        icon={
                                          p.status === 'pass' ? (
                                            <CheckCircleOutlined />
                                          ) : (
                                            <ExclamationCircleOutlined />
                                          )
                                        }
                                      >
                                        {p.status.toUpperCase()}
                                      </Tag>
                                      <Text>{p.label}</Text>
                                    </Space>
                                    <ClaimBlock claim={{ text: p.detail, evidence: p.evidence }} />
                                  </div>
                                ))}
                              </div>
                            )}
                            {(data.crossModuleImpact.onApprove.length > 0 ||
                              data.crossModuleImpact.onReject.length > 0) && (
                              <div>
                                <SectionTitle>Cross-Module Impact</SectionTitle>
                                {data.crossModuleImpact.onApprove.length > 0 && (
                                  <>
                                    <Text type="secondary">If approved</Text>
                                    {data.crossModuleImpact.onApprove.map((c, i) => (
                                      <ClaimBlock key={`oa-${i}`} claim={c} />
                                    ))}
                                  </>
                                )}
                                {data.crossModuleImpact.onReject.length > 0 && (
                                  <>
                                    <Text type="secondary">If rejected</Text>
                                    {data.crossModuleImpact.onReject.map((c, i) => (
                                      <ClaimBlock key={`or-${i}`} claim={c} />
                                    ))}
                                  </>
                                )}
                              </div>
                            )}
                            {data.questionsToAsk.length > 0 && (
                              <div>
                                <SectionTitle>Questions the Approver Should Ask</SectionTitle>
                                {data.questionsToAsk.map((c, i) => (
                                  <ClaimBlock key={`q-${i}`} claim={c} />
                                ))}
                              </div>
                            )}
                            {data.gaps.length > 0 && (
                              <div>
                                <SectionTitle>Data Gaps</SectionTitle>
                                {data.gaps.map((c, i) => (
                                  <ClaimBlock key={`g-${i}`} claim={c} />
                                ))}
                              </div>
                            )}
                          </Space>
                        ),
                      },
                    ]}
                  />

                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Generated {new Date(data.generatedAt).toLocaleString()}
                    {data.dataAsOf ? ` · Data as of ${new Date(data.dataAsOf).toLocaleString()}` : ''} · Grounded in
                    ERP data only · AI never approves or rejects
                  </Text>
                </Space>
              ),
            },
          ]}
        />
      )}
    </Card>
  );
};
